import React from 'react';
import { DateRange as DateRangeCalendar, RangeKeyDict, Range, RangeFocus } from 'react-date-range';
import type { Locale } from 'date-fns';
import { subMonths } from 'date-fns';
import { CalendarNavigator, YearGridState, MonthGridState, ChangeShownDate } from 'components/common/calendar/CalendarNavigator.component';
import { sameOrAfter } from 'components/common/calendar/calendarHelpers';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CalendarPickerPanelProps {
    /** When true only the left panel is rendered (single-month / single-date mode). */
    singlePanel?:        boolean;
    // Panel 0 (left)
    calendarLeftKey:     number;
    showLeftDate:        Date;
    onShownDateChange0:  (date: Date) => void;
    // Panel 1 (right) – ignored when singlePanel is true
    calendarRightKey:    number;
    showRightDate:       Date;
    onShownDateChange1:  (date: Date) => void;
    // Shared calendar config
    ranges:              Range[];
    onChange:            (rangesByKey: RangeKeyDict) => void;
    locale:              Locale;
    focusedRange:        RangeFocus;
    onRangeFocusChange:  (focusedRange: RangeFocus) => void;
    preview?:            { startDate: Date; endDate: Date; color: string };
    onPreviewChange:     (date: Date | null) => void;
    minDate?:            Date;
    // Navigator grid state (passed through to CalendarNavigator)
    yearGrid:            YearGridState  | null;
    monthGrid:           MonthGridState | null;
    setYearGrid:         (grid: YearGridState  | null) => void;
    setMonthGrid:        (grid: MonthGridState | null) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CalendarPickerPanel: React.FC<CalendarPickerPanelProps> = ({
    singlePanel = false,
    calendarLeftKey, showLeftDate, onShownDateChange0,
    calendarRightKey, showRightDate, onShownDateChange1,
    ranges, onChange, locale, focusedRange, onRangeFocusChange,
    preview, onPreviewChange, minDate,
    yearGrid, monthGrid, setYearGrid, setMonthGrid,
}) => {
    // If the second calendar's month is the same as or before the first, display
    // the first panel one month earlier than the second panel.
    // (Only relevant in dual-panel mode.)
    const effectiveShownDate = (!singlePanel && sameOrAfter(showLeftDate, showRightDate))
        ? subMonths(showRightDate, 1)
        : showLeftDate;

    const makeNavigatorRenderer = (panelIdx: 0 | 1) => (
        focusedDate: Date,
        changeShownDate: ChangeShownDate,
        calProps: { minDate?: Date; maxDate?: Date; locale?: Locale },
    ) => (
        <CalendarNavigator
            panelIdx={panelIdx}
            focusedDate={focusedDate}
            changeShownDate={changeShownDate}
            locale={calProps.locale}
            yearGrid={yearGrid}
            monthGrid={monthGrid}
            setYearGrid={setYearGrid}
            setMonthGrid={setMonthGrid}
        />
    );

    const sharedCalendarProps = {
        ranges,
        moveRangeOnFirstSelection: false,
        months: 1 as const,
        direction: 'horizontal' as const,
        onChange,
        rangeColors: ['var(--ga-color-primary)'],
        locale,
        showDateDisplay: false,
        focusedRange,
        onRangeFocusChange,
        preview,
        onPreviewChange,
        ...(minDate && { minDate }),
    };

    return (
        <div className="calendar-picker-panel">
            <DateRangeCalendar
                key={'p0-' + calendarLeftKey}
                {...sharedCalendarProps}
                shownDate={effectiveShownDate}
                onShownDateChange={onShownDateChange0}
                navigatorRenderer={makeNavigatorRenderer(0)}
            />
            {!singlePanel && (
                <DateRangeCalendar
                    key={'p1-' + calendarRightKey}
                    {...sharedCalendarProps}
                    shownDate={showRightDate}
                    onShownDateChange={onShownDateChange1}
                    navigatorRenderer={makeNavigatorRenderer(1)}
                />
            )}
        </div>
    );
};

export default CalendarPickerPanel;

